import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { buildSeo, SEO_ORIGIN } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, BadgeCheck, Building2, ExternalLink, MapPin, Users } from "lucide-react";
import { getAgenciaPublic } from "@/lib/agencias.functions";
import { listMiembrosPublicosAgencia } from "@/lib/miembros-publicos.functions";
import { listAnunciosPublicos } from "@/lib/anuncios.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/utils";

function cityFromRegion(region: string | null | undefined): string | null {
  if (!region) return null;
  const first = region.split(/[,·\/\-–—]/)[0]?.trim();
  return first || null;
}

export const Route = createFileRoute("/agencia/$id")({
  loader: async ({ params }) => {
    const a = (await getAgenciaPublic({ data: { id: params.id } })) as
      | any
      | null;
    if (!a) throw notFound();
    let total = 0;
    let items: any[] = [];
    try {
      const res = (await listAnunciosPublicos({
        data: { agencia_ids: [params.id], limit: 6, offset: 0, with_count: true },
      })) as { items: any[]; total: number };
      total = res.total ?? 0;
      items = res.items ?? [];
    } catch {
      // ignore — SEO still works without count
    }
    return { agencia: a, total, previewItems: items };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return buildSeo({
        title: "Agencia no encontrada | Bizaclia",
        description: "Esta agencia no está disponible en Bizaclia.",
        path: `/agencia/${params.id}`,
        noindex: true,
      });
    }
    const a = loaderData.agencia;
    const total = loaderData.total;
    const city = cityFromRegion(a.region);
    const path = `/agencia/${params.id}`;
    const isInactive = a.estado === "inactiva";

    const titleBase = city
      ? `${a.nombre} — Locales y traspasos en ${city} | Bizaclia`
      : `${a.nombre} — Locales y traspasos de negocios | Bizaclia`;
    const title = titleBase.length > 65 ? titleBase.slice(0, 62) + "…" : titleBase;

    const descBase = city
      ? `${a.nombre}: agencia especializada en traspasos, alquiler y venta de locales en ${city}.${
          total > 0 ? ` ${total} anuncios activos.` : ""
        } Contacta a través de Bizaclia.`
      : `${a.nombre}: agencia especializada en traspasos, alquiler y venta de locales y negocios en España.${
          total > 0 ? ` ${total} anuncios activos.` : ""
        } Contacta a través de Bizaclia.`;
    const description = descBase.length > 160 ? descBase.slice(0, 157) + "…" : descBase;

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SEO_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Agencias", item: `${SEO_ORIGIN}/agencia` },
        { "@type": "ListItem", position: 3, name: a.nombre, item: `${SEO_ORIGIN}${path}` },
      ],
    };

    const agent: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: a.nombre,
      url: `${SEO_ORIGIN}${path}`,
    };
    if (a.logo_url) agent.image = a.logo_url;
    if (a.url_web) agent.sameAs = [a.url_web];
    if (city) {
      agent.address = {
        "@type": "PostalAddress",
        addressLocality: city,
        addressCountry: "ES",
      };
      agent.areaServed = a.region ?? city;
    } else if (a.region) {
      agent.areaServed = a.region;
    }
    if (a.telefono) agent.telephone = a.telefono;
    if (a.email) agent.email = a.email;

    return buildSeo({
      title,
      description,
      path,
      type: "profile",
      image: a.logo_url ?? undefined,
      imageAlt: `Logo de ${a.nombre}`,
      noindex: isInactive,
      jsonLd: [agent, breadcrumb],
    });
  },
  component: AgenciaDetailPage,
  errorComponent: ({ error }) => (
    <main className="container mx-auto max-w-3xl px-4 py-16">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="container mx-auto max-w-3xl px-4 py-16">
      <p className="text-muted-foreground">Agencia no encontrada.</p>
    </main>
  ),
});


function AgenciaDetailPage() {
  const { id } = Route.useParams();
  const { agencia: initialAg, previewItems, total: initialTotal } = Route.useLoaderData();
  const getAg = useServerFn(getAgenciaPublic);
  const listAn = useServerFn(listAnunciosPublicos);

  const qAg = useQuery({
    queryKey: ["agencia-public", id],
    queryFn: async () => {
      const a = await getAg({ data: { id } });
      if (!a) throw notFound();
      return a;
    },
    initialData: initialAg,
  });

  const listEquipo = useServerFn(listMiembrosPublicosAgencia);
  const qEquipo = useQuery({
    queryKey: ["agencia-public-equipo", id],
    queryFn: () => listEquipo({ data: { agencia_id: id } }),
    enabled: !!qAg.data,
  });

  const qAn = useQuery({
    queryKey: ["agencia-public-anuncios", id],
    queryFn: () =>
      listAn({
        data: { agencia_ids: [id], limit: 100, offset: 0, with_count: true },
      }),
    enabled: !!qAg.data,
    initialData:
      previewItems && previewItems.length
        ? { items: previewItems, total: initialTotal }
        : undefined,
  });

  if (qAg.isLoading) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-10">
        <p className="text-muted-foreground">Cargando…</p>
      </main>
    );
  }
  if (!qAg.data) return null;
  const a = qAg.data;
  const items = qAn.data?.items ?? [];
  const city = cityFromRegion(a.region);

  return (
    <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12">
      <Link
        to="/agencia"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Todas las agencias
      </Link>

      <section className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="h-24 w-24 shrink-0 rounded-xl bg-muted overflow-hidden flex items-center justify-center border">
          {a.logo_url ? (
            <img
              src={a.logo_url}
              alt={`Logo de ${a.nombre}`}
              className="h-full w-full object-contain"
            />
          ) : (
            <Building2 className="h-10 w-10 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              {a.nombre}
            </h1>
            {a.estado === "activa" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                <BadgeCheck className="h-4 w-4" />
                Verificada
              </span>
            )}
          </div>
          {city && (
            <p className="mt-1 text-sm text-muted-foreground">
              Agencia inmobiliaria en {city}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {a.region && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-4 w-4" /> {a.region}
              </span>
            )}
            {a.num_empleados != null && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-4 w-4" /> {a.num_empleados} personas
              </span>
            )}
            {a.url_web && (
              <a
                href={a.url_web}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" /> Web
              </a>
            )}
          </div>
          {a.descripcion_publica && (
            <p className="mt-4 text-muted-foreground max-w-3xl whitespace-pre-line">
              {a.descripcion_publica}
            </p>
          )}
        </div>
      </section>

      {(qEquipo.data?.length ?? 0) > 0 && (
        <section className="mt-10">
          <h2 className="font-display font-semibold text-2xl mb-4">Equipo</h2>
          <div className="flex flex-wrap gap-3">
            {qEquipo.data!.map((m) => {
              const full = [m.nombre, m.apellidos].filter(Boolean).join(" ");
              return (
                <Link
                  key={m.slug}
                  to="/@{$slug}"
                  params={{ slug: m.slug }}
                  className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 hover:shadow-md transition-shadow"
                >
                  <div className="h-10 w-10 rounded-full bg-muted overflow-hidden flex items-center justify-center border">
                    {m.foto_url ? (
                      <img src={m.foto_url} alt={`Foto de ${full}`} className="h-full w-full object-cover" />
                    ) : (
                      <Users className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight">{full}</div>
                    {m.cargo && <div className="text-xs text-muted-foreground">{m.cargo}</div>}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display font-semibold text-2xl">
            Anuncios de {a.nombre}
          </h2>
          <span className="text-sm text-muted-foreground">
            {qAn.data?.total ?? 0} anuncios
          </span>
        </div>

        {qAn.isLoading ? (
          <p className="text-muted-foreground">Cargando anuncios…</p>
        ) : items.length === 0 ? (
          <div className="rounded-lg border bg-muted/30 p-6">
            <p className="text-muted-foreground">
              Esta agencia aún no tiene anuncios publicados en Bizaclia.
              Descubre otros locales y negocios disponibles en el portal.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/portal">Explorar el portal</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/agencia">Ver otras agencias</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((row: any) => (
              <AnuncioMiniCard key={row.id} row={row} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function AnuncioMiniCard({ row }: { row: any }) {
  const op = (row.operacion ?? "").toLowerCase();
  const title =
    (row.usos_local?.[0] ?? row.tipo_local ?? "Local") +
    (row.operacion ? ` en ${row.operacion}` : "") +
    (row.barrio
      ? ` en ${row.barrio}, ${row.ciudad}`
      : row.ciudad
        ? ` en ${row.ciudad}`
        : "");

  return (
    <Link to="/local/$id" params={{ id: row.id }} search={{} as never} className="group">
      <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
        <div className="aspect-[16/10] bg-muted overflow-hidden">
          {row.cover_url ? (
            <img
              src={row.cover_url}
              alt={title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
        <CardContent className="p-4 space-y-1.5">
          <h3 className="font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
            {title}
          </h3>
          <div className="text-sm font-bold">
            {op === "traspaso" && row.precio_traspaso != null
              ? `${formatPrice(row.precio_traspaso)} € / Traspaso`
              : op === "venta" && row.precio_venta != null
                ? `${formatPrice(row.precio_venta)} € / Venta`
                : op === "alquiler" && row.precio_alquiler != null
                  ? `${formatPrice(row.precio_alquiler)} € / Alquiler`
                  : op === "rentabilidad" && row.precio_venta != null
                    ? `${formatPrice(row.precio_venta)} € / Venta`
                    : "Consultar"}
          </div>
          {row.direccion_completa && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {row.direccion_completa}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
